import type { RcFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { alertColors, texts } from './theme';
import { AnimalForAdoptionStatus, SupportedLanguage } from '../context/animalHeaven.types';
import { TCountryCode, countries } from 'countries-list';
import { getEmojiFlag } from 'countries-list';
import { TermsTypes } from '../views/Terms/index';

dayjs.extend(customParseFormat);

export const passwordRegex = /^(?=.*[0-9])(?=.*[a-zA-Z])(?!.*\s)[0-9a-zA-Z\W]{8,}$/;
const nameRegex = /^[\p{L} ]+$/u;

export const getBase64 = (img: RcFile | null) =>
  new Promise((resolve, reject) => {
    if (!img) {
      resolve('');
    } else {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.readAsDataURL(img);
    }
  });

export const isValidName = (name: string) => nameRegex.test(name) && name.trim().length !== 0;

export const getTrimmedString = (str: string | undefined) =>
  (str || '').replace(/\s+/g, ' ').trim();

export const getFormattedDateToShow = (date?: string | number | Dayjs, inShortDate?: boolean) => {
  const format = inShortDate ? 'DD/MM/YY' : 'MMMM D, YYYY';
  return date
    ? typeof date === 'string'
      ? dayjs(date, 'YYYY-MM-DD').format(format)
      : typeof date === 'number'
        ? dayjs(date).format(format)
        : date.format(format)
    : '';
};

export const getAllCountries = (): {
  isoCode: string;
  name: string;
  flag: string;
  phoneCode: string;
}[] => {
  const countriesToReturn: { isoCode: string; name: string; flag: string; phoneCode: string }[] =
    [];
  Object.keys(countries).forEach((countryCode: string) => {
    countriesToReturn.push({
      isoCode: countryCode,
      name: countries[countryCode as TCountryCode].native,
      flag: getEmojiFlag(countryCode as TCountryCode),
      phoneCode: '+' + countries[countryCode as TCountryCode].phone[0].toString(),
    });
  });

  return countriesToReturn.sort((a, b) => a.name.localeCompare(b.name));
};

export const validateURLHttps = (url: string) => {
  const regex = /^https:\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|]$/;

  return regex.test(url);
};

export const memorialStatus: {
  [key: string]: {
    color: string;
    id: string;
    keyToTranslate: string;
  };
} = {
  paymentPending: {
    color: alertColors.warning,
    id: 'paymentPending',
    keyToTranslate: 'myMemorials.memorialStatus.paymentPending',
  },
  published: {
    color: alertColors.success,
    id: 'published',
    keyToTranslate: 'myMemorials.memorialStatus.published',
  },
  hidden: {
    color: texts.text3,
    id: 'hidden',
    keyToTranslate: 'myMemorials.memorialStatus.hidden',
  },
};

export const isValidFloatPositiveNumber = (value: string) =>
  // Valid values: 1, | 1,12 | 1
  (value.slice(-1) === ',' ? /^[0-9]+[,]?$/ : /^[0-9]+([,][0-9]+)?$/).test(value);

type NamePathType =
  | 'admin'
  | 'adoption-center'
  | 'adoption-centers'
  | 'animals-for-adoption'
  | 'animals-in-heaven'
  | 'last-animals-in-heaven'
  | 'mission'
  | 'my-animals-for-adoption'
  | 'my-memorials'
  | 'terms'
  | 'view-my-adoption-center';

export const getNamePathToPath = (
  namePath: NamePathType,
  { id, from, termType }: { id?: string; from?: string; termType?: TermsTypes } = {}
): string => {
  const namePathToPath: Map<NamePathType, string> = new Map([
    ['admin', '/admin'],
    ['adoption-center', `/adoption-center/${id}?from=${from}&page=1&pageSize=6&sortBy=createdAt`],
    ['adoption-centers', '/adoption-centers?page=1&pageSize=6&sortBy=createdAt'],
    ['animals-for-adoption', '/animals-for-adoption?page=1&pageSize=6&sortBy=createdAt'],
    ['animals-in-heaven', '/animals-in-heaven?page=1&pageSize=6&sortBy=createdAt'],
    ['last-animals-in-heaven', '/last-animals-in-heaven'],
    ['mission', '/mission?page=1&pageSize=6&sortBy=createdAt'],
    ['my-animals-for-adoption', '/my-animals-for-adoption?page=1&pageSize=6&petName=&status='],
    ['my-memorials', '/my-memorials?page=1&pageSize=6&status='],
    ['terms', `/terms?termType=${termType}`],
    ['view-my-adoption-center', '/view-my-adoption-center'],
  ]);

  return namePathToPath.get(namePath) as string;
};

export const animalForAdoptionStatus: {
  [key in AnimalForAdoptionStatus]: {
    color: string;
    id: string;
    keyToTranslate: string;
  };
} = {
  hidden: {
    color: texts.text3,
    id: 'hidden',
    keyToTranslate: 'myMemorials.memorialStatus.hidden',
  },
  published: {
    color: alertColors.success,
    id: 'published',
    keyToTranslate: 'myMemorials.memorialStatus.published',
  },
};

export const unitsForAge: ['years', 'months'] = ['years', 'months'];

export const supportedLanguages = [
  'bg',
  'ca',
  'cs',
  'da',
  'de',
  'el',
  'en_gb',
  'en_us',
  'es_es',
  'es_va',
  'eu',
  'fi',
  'fr',
  'gl',
  'hu',
  'it',
  'ja',
  'ko',
  'nl',
  'no',
  'pl',
  'pt_br',
  'pt_pt',
  'ro',
  'ru',
  'sv',
  'tr',
  'uk',
  'zh',
];

/**
 * Get language from navigator, if the language is different
 * from English or Spanish return 'es' (Spanish)
 *
 * @return {string}
 */
export const getLanguageFromNavigator = (): SupportedLanguage => {
  const language = navigator.language;

  if (supportedLanguages.includes(language)) {
    return language;
  } else if (navigator.language.slice(0, 2) === 'es') {
    return 'es_es';
  } else {
    return 'en_us';
  }
};

export const getTranslation = async (language: SupportedLanguage) => {
  const files = [
    'account.json',
    'addCommentNotLoggedUser.json',
    'addMemorial.json',
    'adoptionCenter.json',
    'adoptionCenterDetails.json',
    'adoptionCenters.json',
    'animalForAdoption.json',
    'animalForAdoptionForm.json',
    'animalsForAdoptionList.json',
    'animalsInHeaven.json',
    'appBar.json',
    'captcha.json',
    'changeDate.json',
    'common.json',
    'confirmAccount.json',
    'contactUs.json',
    'footer.json',
    'helmet.json',
    'informationFromPeopleWhoWantToAdopt.json',
    'login.json',
    'memorial.json',
    'mission.json',
    'myAdoptionCenter.json',
    'myMemorials.json',
    'pageNotFound.json',
    'pagination.json',
    'qr.json',
    'upload.json',
  ];
  const promises = files.map((e) => import(`../translations/${language}/${e}`));
  const results = await Promise.allSettled(promises);
  let translation = {};
  results.forEach((e) => {
    if (e.status === 'rejected') {
      console.error('Error loading translation', e.reason);
    } else {
      translation = { ...translation, ...e.value.default };
    }
  });
  return translation;
};

export const showGlobalSpinner = () => {
  const initialLoader = document.getElementById('initial-loader-container');
  const root = document.getElementById('root');
  if (root && initialLoader) {
    root.style.display = 'none';
    initialLoader.style.display = 'flex';
  }
};

export const hideGlobalSpinner = () => {
  const initialLoader = document.getElementById('initial-loader-container');
  const root = document.getElementById('root');
  if (root && initialLoader) {
    root.style.display = 'block';
    initialLoader.style.display = 'none';
  }
};

export const gotToHomeInWordPress = () => window.open('https://animal-heaven.org/', '_blank');
export const gotToUsInWordPress = () => window.open('https://animal-heaven.org/', '_blank');
export const blogURL = 'https://animal-heaven.org/';
export const gotToBlogInWordPress = () => window.open(blogURL, '_blank');
